import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Clock, Calendar, ArrowRight } from "lucide-react";
import SEO, { articleSchema, breadcrumbSchema, SITE_URL } from "@/components/SEO";

function mdToHtml(md) {
    if (!md) return "";
    // Very light markdown renderer — enough for our seed content
    let html = md
        .replace(/^### (.*)$/gm, '<h3 class="font-serif text-2xl mt-10 mb-3 text-rk-navy">$1</h3>')
        .replace(/^## (.*)$/gm, '<h2 class="font-serif text-3xl mt-12 mb-4 text-rk-navy">$1</h2>')
        .replace(/^# (.*)$/gm, '<h1 class="font-serif text-4xl md:text-5xl mt-6 mb-6 text-rk-navy font-light">$1</h1>')
        .replace(/\*\*(.+?)\*\*/g, '<strong class="text-rk-navy font-semibold">$1</strong>')
        .replace(/\*(.+?)\*/g, '<em class="italic">$1</em>')
        .replace(/^> (.*)$/gm, '<blockquote class="border-l-2 border-rk-gold pl-5 py-2 my-6 font-serif text-xl italic text-rk-navy/80">$1</blockquote>')
        .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-rk-gold-deep rk-link-gold font-semibold">$1</a>')
        .replace(/^---$/gm, '<hr class="rk-divider-gold my-10" />')
        .replace(/^\| (.+) \|$/gm, (row) => {
            const cells = row.slice(2, -2).split(" | ");
            return "<tr>" + cells.map(c => `<td class="border-b border-rk-border px-3 py-2 text-sm">${c}</td>`).join("") + "</tr>";
        });
    html = html.replace(/((?:<tr>.*?<\/tr>\s*)+)/gs, '<table class="w-full my-6 text-sm">$1</table>');
    // Lists
    html = html.replace(/(^|\n)(- .+(?:\n- .+)*)/g, (_m, pre, block) => {
        const items = block.split("\n").map(l => `<li class="mb-2">${l.replace(/^- /, "")}</li>`).join("");
        return `${pre}<ul class="list-disc pl-6 my-4 text-rk-navy/80 leading-relaxed">${items}</ul>`;
    });
    html = html.replace(/(^|\n)(\d+\. .+(?:\n\d+\. .+)*)/g, (_m, pre, block) => {
        const items = block.split("\n").map(l => `<li class="mb-2">${l.replace(/^\d+\.\s*/, "")}</li>`).join("");
        return `${pre}<ol class="list-decimal pl-6 my-4 text-rk-navy/80 leading-relaxed">${items}</ol>`;
    });
    // Paragraphs
    html = html.split(/\n\n+/).map(p => {
        if (p.match(/^<(h\d|ul|ol|table|blockquote|hr|div)/)) return p;
        if (!p.trim()) return "";
        return `<p class="text-rk-navy/80 leading-relaxed my-4 text-[17px]">${p}</p>`;
    }).join("\n");
    return html;
}

export function BlogList() {
    const [posts, setPosts] = useState([]);
    useEffect(() => {
        api.get("/blog?limit=30").then(r => setPosts(r.data.items));
    }, []);
    return (
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 lg:px-12 py-12">
            <SEO
                title="Gifting Journal — Ideas, Guides & Stories"
                description="Thoughtful essays on gifting, anniversaries, Diwali, corporate gifting and the science of why gift cards work. From LoyltyKart's editors."
                path="/blog"
                keywords="gifting guide, gift cards india, corporate gifting, diwali gift ideas, anniversary gifts, wedding shagun"
                structuredData={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Journal", path: "/blog" }])}
            />
            <div className="mb-10">
                <div className="text-[10px] uppercase tracking-[0.3em] text-rk-gold-deep mb-3">LoyltyKart</div>
                <h1 className="font-serif text-5xl md:text-6xl text-rk-navy font-light">The Gifting Journal</h1>
                <div className="w-16 rk-divider-gold mt-5" />
                <p className="mt-5 text-rk-muted max-w-2xl text-lg">
                    Ideas, essays and guides on the art of gifting, from our editors and guest writers.
                </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
                {posts.map((p, i) => (
                    <Link
                        to={`/blog/${p.slug}`}
                        key={p.slug}
                        data-testid={`blog-card-${p.slug}`}
                        className={`group bg-white border border-rk-border p-0 overflow-hidden rk-card ${i === 0 ? "md:col-span-2" : ""}`}
                    >
                        <div className={`relative overflow-hidden ${i === 0 ? "aspect-[16/7]" : "aspect-[16/9]"}`}>
                            <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        </div>
                        <div className="p-6 md:p-8">
                            <div className="text-[10px] uppercase tracking-[0.26em] text-rk-gold-deep mb-3">{p.category}</div>
                            <h2 className={`font-serif text-rk-navy leading-tight mb-3 ${i === 0 ? "text-3xl md:text-4xl" : "text-2xl"}`}>{p.title}</h2>
                            <p className="text-rk-navy/70 text-sm leading-relaxed mb-4">{p.excerpt}</p>
                            <div className="flex items-center gap-4 text-xs text-rk-muted uppercase tracking-wider">
                                <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {new Date(p.published_at).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })}</span>
                                <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {p.read_min} min read</span>
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}

export function BlogPost() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const nav = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/blog/${slug}`);
                setData(r.data);
                window.scrollTo({ top: 0 });
            } catch { nav("/blog"); }
        })();
    }, [slug, nav]);

    if (!data) return <div className="py-20 text-center text-rk-muted">Loading…</div>;
    const { post, related } = data;

    return (
        <article className="bg-rk-bg">
            <SEO
                title={post.title}
                description={post.excerpt}
                path={`/blog/${slug}`}
                image={post.cover}
                type="article"
                keywords={(post.tags || []).join(", ")}
                structuredData={[
                    articleSchema(post),
                    breadcrumbSchema([
                        { name: "Home", path: "/" },
                        { name: "Journal", path: "/blog" },
                        { name: post.title.slice(0, 60), path: `/blog/${slug}` },
                    ]),
                ]}
            />
            <div className="max-w-[820px] mx-auto px-4 md:px-8 pt-10 md:pt-14">
                <Link to="/blog" className="text-xs uppercase tracking-[0.26em] text-rk-muted hover:text-rk-navy inline-flex items-center gap-1.5">
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to Journal
                </Link>
                <div className="mt-6">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-rk-gold-deep mb-3">{post.category}</div>
                    <h1 className="font-serif text-4xl md:text-6xl text-rk-navy leading-[1.05] font-light">{post.title}</h1>
                    <div className="mt-5 flex items-center gap-4 text-xs text-rk-muted uppercase tracking-wider">
                        <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {new Date(post.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
                        <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {post.read_min} min read</span>
                    </div>
                </div>
            </div>
            {post.cover && (
                <div className="max-w-[1100px] mx-auto mt-10 px-4 md:px-8">
                    <img src={post.cover} alt={post.title} className="w-full aspect-[16/7] object-cover" />
                </div>
            )}
            <div className="max-w-[740px] mx-auto px-4 md:px-8 py-12 prose-lg">
                <div dangerouslySetInnerHTML={{ __html: mdToHtml(post.content_md) }} />
            </div>
            {related && related.length > 0 && (
                <section className="max-w-[1200px] mx-auto px-4 md:px-8 lg:px-12 pb-20 border-t border-rk-border pt-12">
                    <h3 className="font-serif text-3xl text-rk-navy mb-6">More reading</h3>
                    <div className="grid md:grid-cols-3 gap-5">
                        {related.map(r => (
                            <Link to={`/blog/${r.slug}`} key={r.slug} className="group bg-white border border-rk-border rk-card overflow-hidden">
                                <div className="aspect-[16/9] overflow-hidden">
                                    <img src={r.cover} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                </div>
                                <div className="p-5">
                                    <div className="text-[9px] uppercase tracking-[0.26em] text-rk-gold-deep mb-2">{r.category}</div>
                                    <h4 className="font-serif text-xl text-rk-navy leading-snug line-clamp-2">{r.title}</h4>
                                    <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-rk-navy font-semibold rk-link-gold inline-flex items-center gap-1.5">
                                        Read <ArrowRight className="w-3 h-3" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}
        </article>
    );
}
